'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import {
  ShoppingBag,
  RefreshCw,
  Wrench,
  Stethoscope,
  Car,
  Sparkles,
  MessageSquare,
  Search,
  Lightbulb,
  Info,
  Globe,
  Tag,
  LayoutGrid
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useLanguage } from '@/context/LanguageContext';
import { useCountry, COUNTRIES } from '@/context/CountryContext';
import { TIPS_DATABASE, getContextualTips } from '@/app/lib/tips-data';
import { AIPersonalizedRecommendations } from '@/components/AIPersonalizedRecommendations';

export default function Home() {
  const { t, isRTL, lang } = useLanguage();
  const { setSelectedCountryCode, selectedCountry } = useCountry();

  const [currentTipIndex, setCurrentTipIndex] = useState(0);
  const activeTips = TIPS_DATABASE;

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTipIndex((prev) => (prev + 1) % activeTips.length);
    }, 10000);
    return () => clearInterval(interval);
  }, [activeTips.length]);

  const mainServices = [
    { id: 'buy', label: t('buy_car'), sub: 'Buy Car', icon: Car, color: 'text-blue-600 bg-blue-50', href: '/buy' },
    { id: 'sell', label: t('sell_car'), sub: 'Sell Car', icon: RefreshCw, color: 'text-orange-600 bg-orange-50', href: '/sell' },
    { id: 'marketplace', label: 'السوق العام', sub: 'Public Market', icon: Tag, color: 'text-emerald-600 bg-emerald-50', href: '/marketplace' },
    { id: 'parts', label: t('parts'), sub: 'Spare Parts', icon: ShoppingBag, color: 'text-blue-600 bg-blue-50', href: '/parts' },
    { id: 'maintenance', label: t('maintenance'), sub: 'Workshops', icon: Wrench, color: 'text-orange-600 bg-orange-50', href: '/maintenance' },
    { id: 'services', label: 'الخدمات المتكاملة', sub: 'Hub', icon: LayoutGrid, color: 'text-indigo-600 bg-indigo-50', href: '/services' },
  ];

  const aiServices = [
    { id: 'diagnose', label: t('diagnose'), sub: 'AI Diagnose', icon: Stethoscope, color: 'text-emerald-600 bg-emerald-50', href: '/diagnose' },
    { id: 'inspection', label: t('inspection'), sub: 'AI Vision', icon: Sparkles, color: 'text-indigo-600 bg-indigo-50', href: '/inspection' },
    { id: 'advisor', label: 'المستشار الذكي', sub: 'Car Fit Advisor', icon: Car, color: 'text-yellow-600 bg-yellow-50', href: '/buy/advisor' },
  ];

  return (
    <div className="flex flex-col w-full">
      <header className="md:hidden p-4 flex items-center justify-between bg-[#EEF2FF] border-b border-indigo-100 sticky top-0 z-50 shadow-sm backdrop-blur-md bg-opacity-95 -mx-4">
        <div className="flex items-center gap-2">
          <div className="bg-primary p-2 rounded-xl shadow-lg relative overflow-hidden">
            <Car className="h-6 w-6 text-white" />
          </div>
          <h1 className="text-xl font-black text-slate-800 leading-none">
            سيارتي<span className="text-primary">360</span>
          </h1>
        </div>

        <div className="flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="p-2 rounded-full bg-white text-primary border border-indigo-50 shadow-sm">
                <Globe className="h-5 w-5" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48 rounded-2xl p-2 shadow-xl border-none">
              {COUNTRIES.map((c) => (
                <DropdownMenuItem key={c.code} onClick={() => setSelectedCountryCode(c.code)} className="font-bold">
                  {c.name}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <Link href="/chat">
            <div className="p-2 rounded-full bg-white text-slate-500 border border-indigo-50 shadow-sm">
              <MessageSquare className="h-5 w-5" />
            </div>
          </Link>
        </div>
      </header>

      <div className="space-y-10 py-6 max-w-4xl mx-auto w-full">
        <section className="relative group px-2">
          <Search className={`absolute ${isRTL ? 'right-6' : 'left-6'} top-1/2 -translate-y-1/2 text-slate-300 h-5 w-5 transition-colors group-focus-within:text-primary`} />
          <input
            type="text"
            placeholder={t('search_placeholder')}
            className={`w-full h-16 bg-white border-none shadow-md rounded-[2rem] ${isRTL ? 'pr-14 pl-6' : 'pl-14 pr-6'} text-sm font-bold focus:ring-4 focus:ring-primary/10 transition-all`}
          />
        </section>

        {/* AI Recommendations Hub (Behavior based) */}
        <section className="px-2">
          <AIPersonalizedRecommendations />
        </section>

        <div className="md:hidden flex items-center gap-2 px-4 py-2 bg-indigo-50/50 rounded-2xl border border-indigo-50 mx-2 overflow-hidden">
          <Sparkles className="h-4 w-4 text-primary shrink-0 animate-pulse" />
          <p className="text-[10px] font-bold text-slate-600 truncate">
            {activeTips[currentTipIndex].text_ar}
          </p>
        </div>

        <section className="space-y-4">
          <h2 className="text-xs font-black text-slate-400 uppercase tracking-widest px-4">{t('main_services')}</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 px-2">
            {mainServices.map((service) => (
              <Link key={service.id} href={service.href} prefetch={true}>
                <Card className="h-full flex flex-col items-center justify-center p-6 border-none shadow-sm bg-white rounded-[2.5rem] hover:shadow-xl hover:-translate-y-1 transition-all group">
                  <div className={`p-5 rounded-2xl mb-4 ${service.color} transition-colors group-hover:bg-primary group-hover:text-white shadow-sm`}>
                    <service.icon className="h-8 w-8" />
                  </div>
                  <span className="text-lg font-bold text-slate-800 font-headline mb-0.5 text-center">{service.label}</span>
                  <span className="text-[9px] text-slate-400 font-bold uppercase tracking-widest text-center">{service.sub}</span>
                </Card>
              </Link>
            ))}
          </div>
        </section>

        <section className="space-y-4">
          <div className="flex items-center gap-2 px-4">
            <Sparkles className="h-5 w-5 text-indigo-500" />
            <h2 className="text-xs font-black text-slate-400 uppercase tracking-widest">{t('ai_services')}</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 px-2">
            {aiServices.map((service) => (
              <Link key={service.id} href={service.href} prefetch={true}>
                <Card className="p-8 border-none shadow-md bg-gradient-to-br from-white to-slate-50 rounded-[2.5rem] relative overflow-hidden group hover:shadow-xl transition-all h-full flex items-center gap-6">
                  <div className={`p-5 rounded-2xl ${service.color} shadow-sm group-hover:scale-110 transition-transform`}>
                    <service.icon className="h-8 w-8" />
                  </div>
                  <div className="flex flex-col">
                    <span className="text-xl font-bold text-slate-800">{service.label}</span>
                    <span className="text-xs text-slate-400 font-bold uppercase tracking-widest">{service.sub}</span>
                  </div>
                  <div className="mr-auto opacity-10 group-hover:opacity-20 transition-opacity">
                    <service.icon className="h-16 w-16" />
                  </div>
                </Card>
              </Link>
            ))}
          </div>
        </section>

        <section className="px-2">
          <Dialog>
            <DialogTrigger asChild>
              <Button variant="outline" className="w-full h-16 rounded-[2rem] border-dashed border-indigo-200 bg-white hover:bg-indigo-50 flex items-center justify-center gap-3 group">
                <div className="p-2 bg-indigo-50 rounded-full group-hover:bg-indigo-100 transition-colors">
                  <Lightbulb className="h-6 w-6 text-primary" />
                </div>
                <div className="text-right">
                  <p className="text-sm font-bold text-slate-800">اعطني نصائح مخصصة</p>
                  <p className="text-[10px] text-slate-400 font-bold uppercase">Smart Advice System</p>
                </div>
              </Button>
            </DialogTrigger>
            <DialogContent className="rounded-[2.5rem] p-8 max-w-md border-none shadow-2xl">
              <DialogHeader className="mb-6">
                <DialogTitle className="text-2xl font-black flex items-center gap-3 text-slate-800">
                  <div className="p-3 bg-indigo-50 rounded-2xl">
                    <Sparkles className="h-6 w-6 text-primary" />
                  </div>
                  نصائح الخبير الذكية
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                {getContextualTips('general', 4).map((tip, idx) => (
                  <div key={tip.id} className="flex gap-4 p-4 bg-slate-50 rounded-2xl border border-slate-100 animate-in fade-in slide-in-from-bottom-2" style={{ animationDelay: `${idx * 100}ms` }}>
                    <div className="h-8 w-8 rounded-full bg-white shadow-sm flex items-center justify-center shrink-0">
                      <Info className="h-4 w-4 text-primary" />
                    </div>
                    <p className="text-sm font-bold text-slate-700 leading-relaxed">{tip.text_ar}</p>
                  </div>
                ))}
              </div>
            </DialogContent>
          </Dialog>
        </section>
      </div>
    </div>
  );
}
